import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from 'components/ui/card';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Badge } from 'components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  ArrowLeft, Calendar, Clock, Plus, Check, X, 
  Palmtree, Heart, User, HelpCircle, Send, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

export default function TimeOffRequests() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [myRequests, setMyRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [showNewRequestModal, setShowNewRequestModal] = useState(false);
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [requestType, setRequestType] = useState('vacation');
  const [reason, setReason] = useState('');
  const [responseNote, setResponseNote] = useState('');

  const isCaregiver = user?.role === 'independent_caregiver';

  useEffect(() => {
    if (isCaregiver) {
      fetchMyRequests();
    } else {
      fetchPendingRequests();
    }
  }, [isCaregiver]);

  const fetchMyRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/time-off/my-requests');
      setMyRequests(response.data.requests || []);
    } catch (error) {
      toast.error('Failed to load your requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/time-off/pending');
      setPendingRequests(response.data.requests || []);
    } catch (error) {
      console.error('Failed to load pending requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitRequest = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select start and end dates');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post('/time-off/request', {
        start_date: startDate,
        end_date: endDate,
        type: requestType,
        reason
      });
      
      toast.success('Time-off request submitted!');
      setShowNewRequestModal(false);
      resetForm();
      fetchMyRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRespond = async (action) => {
    if (!selectedRequest) return;
    
    setSubmitting(true);
    try {
      await api.put(`/time-off/${selectedRequest.request_id}/respond`, {
        action,
        note: responseNote
      });
      
      toast.success(`Request ${action === 'approve' ? 'approved' : 'denied'}`);
      setShowResponseModal(false);
      setSelectedRequest(null);
      setResponseNote('');
      fetchPendingRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to respond');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    try {
      await api.delete(`/time-off/${requestId}`);
      toast.success('Request cancelled');
      fetchMyRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel');
    }
  };

  const resetForm = () => {
    setStartDate('');
    setEndDate('');
    setRequestType('vacation');
    setReason('');
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'vacation': return <Palmtree className="w-4 h-4 text-teal-500" />;
      case 'sick': return <Heart className="w-4 h-4 text-red-500" />;
      case 'personal': return <User className="w-4 h-4 text-purple-500" />;
      default: return <HelpCircle className="w-4 h-4 text-slate-500" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-300">Pending</Badge>;
      case 'approved':
        return <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">Approved</Badge>;
      case 'denied':
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">Denied</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-slate-900">Time-Off Requests</h1>
                <p className="text-xs sm:text-sm text-slate-500">
                  {isCaregiver ? 'Manage your time-off requests' : 'Review caregiver time-off requests'}
                </p>
              </div>
            </div>
            
            {isCaregiver && (
              <Button 
                onClick={() => setShowNewRequestModal(true)}
                className="bg-teal-500 hover:bg-teal-600"
                data-testid="new-time-off-btn"
              >
                <Plus className="w-4 h-4" />
                Request Time Off
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
          </div>
        ) : isCaregiver ? (
          /* Caregiver View - My Requests */
          <div className="space-y-4">
            {myRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No Time-Off Requests</h3>
                  <p className="text-slate-500 mb-4">You haven't submitted any time-off requests yet.</p>
                  <Button onClick={() => setShowNewRequestModal(true)} className="bg-teal-500 hover:bg-teal-600">
                    <Plus className="w-4 h-4" />
                    Request Time Off
                  </Button>
                </CardContent>
              </Card>
            ) : (
              myRequests.map((request) => (
                <Card key={request.request_id} data-testid={`request-${request.request_id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                        {getTypeIcon(request.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-medium text-slate-900 capitalize text-sm sm:text-base">{request.type} Leave</h3>
                          {getStatusBadge(request.status)}
                        </div>
                        <p className="text-xs sm:text-sm text-slate-600 mt-0.5">
                          {formatDate(request.start_date)} - {formatDate(request.end_date)}
                        </p>
                        {request.reason && (
                          <p className="text-xs sm:text-sm text-slate-500 mt-1.5">{request.reason}</p>
                        )}
                        {request.status === 'pending' && (
                          <div className="mt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleCancelRequest(request.request_id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 h-7 text-xs"
                            >
                              Cancel Request
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : (
          /* Family View - Pending Requests to Review */
          <div className="space-y-4">
            {pendingRequests.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">All Caught Up!</h3>
                  <p className="text-slate-500">No pending time-off requests to review.</p>
                </CardContent>
              </Card>
            ) : (
              pendingRequests.map((request) => (
                <Card key={request.request_id} data-testid={`pending-${request.request_id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          {getTypeIcon(request.type)}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <h3 className="font-medium text-slate-900">{request.caregiver_name}</h3>
                            <Badge variant="outline" className="capitalize">{request.type}</Badge>
                          </div>
                          <p className="text-sm text-slate-600">
                            {formatDate(request.start_date)} - {formatDate(request.end_date)}
                          </p>
                          {request.reason && (
                            <p className="text-sm text-slate-500 mt-2">{request.reason}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowResponseModal(true);
                          }}
                          className="border-red-300 text-red-600 hover:bg-red-50"
                          data-testid={`deny-${request.request_id}`}
                        >
                          <X className="w-4 h-4" />
                          Deny
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowResponseModal(true);
                          }}
                          className="bg-green-500 hover:bg-green-600"
                          data-testid={`approve-${request.request_id}`}
                        >
                          <Check className="w-4 h-4" />
                          Approve
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* New Request Modal */}
      <Dialog open={showNewRequestModal} onOpenChange={setShowNewRequestModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-teal-500" />
              Request Time Off
            </DialogTitle>
            <DialogDescription>
              Submit a time-off request to your care families.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date">Start Date</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="start-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date">End Date</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate}
                  data-testid="end-date-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="type">Type of Leave</Label>
              <Select value={requestType} onValueChange={setRequestType}>
                <SelectTrigger data-testid="leave-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacation">
                    <div className="flex items-center gap-2">
                      <Palmtree className="w-4 h-4 text-teal-500" />
                      Vacation
                    </div>
                  </SelectItem>
                  <SelectItem value="sick">
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-red-500" />
                      Sick Leave
                    </div>
                  </SelectItem>
                  <SelectItem value="personal">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-purple-500" />
                      Personal
                    </div>
                  </SelectItem>
                  <SelectItem value="other">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-slate-500" />
                      Other
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="reason">Reason (Optional)</Label>
              <Textarea
                id="reason"
                placeholder="Add any additional details..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="reason-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewRequestModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitRequest}
              disabled={submitting || !startDate || !endDate}
              className="bg-teal-500 hover:bg-teal-600"
              data-testid="submit-request-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Response Modal */}
      <Dialog open={showResponseModal} onOpenChange={setShowResponseModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Respond to Time-Off Request</DialogTitle>
            <DialogDescription>
              {selectedRequest && (
                <>
                  {selectedRequest.caregiver_name} is requesting {selectedRequest.type} leave from{' '}
                  {formatDate(selectedRequest.start_date)} to {formatDate(selectedRequest.end_date)}.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="response-note">Add a Note (Optional)</Label>
              <Textarea
                id="response-note"
                placeholder="Any message for the caregiver..."
                value={responseNote}
                onChange={(e) => setResponseNote(e.target.value)}
                data-testid="response-note-input"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowResponseModal(false)}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleRespond('deny')}
              disabled={submitting}
              className="border-red-300 text-red-600 hover:bg-red-50"
              data-testid="confirm-deny-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
              Deny Request
            </Button>
            <Button
              onClick={() => handleRespond('approve')}
              disabled={submitting}
              className="bg-green-500 hover:bg-green-600"
              data-testid="confirm-approve-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
